import { useEffect, useMemo, useRef, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    IconButton,
    Paper,
    Stack,
    Tab,
    Tabs,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import {
    CloudUpload as UploadIcon,
    ContentCopy as CopyIcon,
    Delete as DeleteIcon,
    Download as DownloadIcon,
    InsertDriveFile as FileIcon,
    NoteAdd as NoteAddIcon,
    Refresh as RefreshIcon,
} from '@mui/icons-material';
import {
    createSharedSnippet,
    deleteSharedFile,
    deleteSharedSnippet,
    getSharedFileDownloadUrl,
    getSharedFiles,
    getSharedSnippets,
    uploadSharedFiles,
} from '../../services/api';

function formatBytes(bytes) {
    if (!Number.isFinite(bytes) || bytes < 0) return '-';
    if (bytes === 0) return '0 B';
    const units = ['B', 'KB', 'MB', 'GB', 'TB'];
    const index = Math.floor(Math.log(bytes) / Math.log(1024));
    const value = bytes / Math.pow(1024, index);
    return `${value.toFixed(value >= 10 || index === 0 ? 0 : 1)} ${units[index]}`;
}

function formatTimestamp(value) {
    if (!value) return '-';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '-';
    return date.toLocaleString();
}

function getDateKey(value) {
    if (!value) return 'unknown';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return 'unknown';
    return date.toISOString().slice(0, 10);
}

function formatDateLabel(key) {
    if (key === 'unknown') return 'Unknown Date';
    const date = new Date(`${key}T00:00:00Z`);
    if (Number.isNaN(date.getTime())) return 'Unknown Date';
    return date.toLocaleDateString();
}

async function copyTextToClipboard(text) {
    if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
        return;
    }

    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.setAttribute('readonly', '');
    textArea.style.position = 'absolute';
    textArea.style.left = '-9999px';
    document.body.appendChild(textArea);
    textArea.select();
    document.execCommand('copy');
    document.body.removeChild(textArea);
}

function SharedFilesManager({ showNotification }) {
    const [files, setFiles] = useState([]);
    const [snippets, setSnippets] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [activeSharedTab, setActiveSharedTab] = useState('snippets');
    const [snippetDialogOpen, setSnippetDialogOpen] = useState(false);
    const [snippetName, setSnippetName] = useState('');
    const [snippetContent, setSnippetContent] = useState('');
    const [snippetSaving, setSnippetSaving] = useState(false);
    const inputRef = useRef(null);

    const fetchSharedItems = async () => {
        try {
            setLoading(true);
            setError(null);
            const [filesData, snippetsData] = await Promise.all([
                getSharedFiles(),
                getSharedSnippets(),
            ]);
            setFiles(filesData.files || []);
            setSnippets(snippetsData.snippets || []);
        } catch (err) {
            console.error('Error fetching shared items:', err);
            setError('Failed to load shared files and snippets.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchSharedItems();
    }, []);

    const groupedFiles = useMemo(() => {
        const sorted = [...files].sort((a, b) => new Date(b.uploaded_at) - new Date(a.uploaded_at));
        const map = new Map();
        sorted.forEach((file) => {
            const key = getDateKey(file.uploaded_at);
            if (!map.has(key)) map.set(key, []);
            map.get(key).push(file);
        });
        return Array.from(map.entries());
    }, [files]);

    const handleUploadClick = () => {
        inputRef.current?.click();
    };

    const handleSharedTabChange = (_event, value) => {
        setActiveSharedTab(value);
    };

    const handleUploadChange = async (event) => {
        const selectedFiles = Array.from(event.target.files || []);
        if (selectedFiles.length === 0) return;

        try {
            setUploading(true);
            await uploadSharedFiles(selectedFiles);
            showNotification?.(`Uploaded ${selectedFiles.length} file(s)`);
            await fetchSharedItems();
        } catch (err) {
            console.error('Error uploading files:', err);
            showNotification?.('Upload failed', 'error');
        } finally {
            setUploading(false);
            event.target.value = '';
        }
    };

    const handleFileDelete = async (file) => {
        const confirmed = window.confirm(`Delete "${file.original_name}"? This cannot be undone.`);
        if (!confirmed) return;

        try {
            await deleteSharedFile(file.id);
            showNotification?.('File deleted');
            await fetchSharedItems();
        } catch (err) {
            console.error('Error deleting file:', err);
            showNotification?.('Delete failed', 'error');
        }
    };

    const handleSnippetSave = async () => {
        if (!snippetContent.trim()) {
            showNotification?.('Snippet content cannot be empty', 'error');
            return;
        }

        try {
            setSnippetSaving(true);
            await createSharedSnippet({
                name: snippetName.trim(),
                content: snippetContent,
            });
            setSnippetDialogOpen(false);
            setSnippetName('');
            setSnippetContent('');
            showNotification?.('Snippet saved');
            await fetchSharedItems();
        } catch (err) {
            console.error('Error saving snippet:', err);
            showNotification?.('Failed to save snippet', 'error');
        } finally {
            setSnippetSaving(false);
        }
    };

    const handleSnippetDelete = async (snippet) => {
        const confirmed = window.confirm(`Delete snippet "${snippet.name}"? This cannot be undone.`);
        if (!confirmed) return;

        try {
            await deleteSharedSnippet(snippet.id);
            showNotification?.('Snippet deleted');
            await fetchSharedItems();
        } catch (err) {
            console.error('Error deleting snippet:', err);
            showNotification?.('Failed to delete snippet', 'error');
        }
    };

    const handleSnippetCopy = async (snippet) => {
        try {
            await copyTextToClipboard(snippet.content);
            showNotification?.(`Copied "${snippet.name}"`);
        } catch (err) {
            console.error('Error copying snippet:', err);
            showNotification?.('Failed to copy snippet', 'error');
        }
    };

    return (
        <Box>
            <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={2}
                alignItems={{ xs: 'stretch', sm: 'center' }}
                sx={{ mb: 2 }}
            >
                <Box sx={{ flex: 1 }}>
                    <Typography variant="h6" fontWeight={600}>
                        File Upload
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Shared snippets and uploaded files for other users.
                    </Typography>
                </Box>
                <input
                    ref={inputRef}
                    type="file"
                    multiple
                    hidden
                    onChange={handleUploadChange}
                />
                {activeSharedTab === 'snippets' ? (
                    <Button
                        variant="contained"
                        startIcon={<NoteAddIcon />}
                        onClick={() => setSnippetDialogOpen(true)}
                    >
                        Add Snippet
                    </Button>
                ) : (
                    <Button
                        variant="contained"
                        startIcon={uploading ? <CircularProgress size={18} color="inherit" /> : <UploadIcon />}
                        onClick={handleUploadClick}
                        disabled={uploading}
                    >
                        {uploading ? 'Uploading...' : 'Upload Files'}
                    </Button>
                )}
                <Button
                    variant="outlined"
                    startIcon={<RefreshIcon />}
                    onClick={fetchSharedItems}
                    disabled={loading}
                >
                    Refresh
                </Button>
            </Stack>

            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            )}

            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
                <Tabs
                    value={activeSharedTab}
                    onChange={handleSharedTabChange}
                    variant="scrollable"
                    scrollButtons="auto"
                    allowScrollButtonsMobile
                    aria-label="Shared file upload tabs"
                >
                    <Tab
                        icon={<NoteAddIcon />}
                        iconPosition="start"
                        label={`Snippets (${snippets.length})`}
                        value="snippets"
                        id="shared-files-tab-snippets"
                        aria-controls="shared-files-panel-snippets"
                    />
                    <Tab
                        icon={<FileIcon />}
                        iconPosition="start"
                        label={`Uploaded Files (${files.length})`}
                        value="files"
                        id="shared-files-tab-files"
                        aria-controls="shared-files-panel-files"
                    />
                </Tabs>
            </Box>

            <Dialog
                open={snippetDialogOpen}
                onClose={() => !snippetSaving && setSnippetDialogOpen(false)}
                fullWidth
                maxWidth="md"
            >
                <DialogTitle>Add Shared Snippet</DialogTitle>
                <DialogContent dividers>
                    <Stack spacing={2}>
                        <TextField
                            label="Snippet Name"
                            value={snippetName}
                            onChange={(event) => setSnippetName(event.target.value)}
                            fullWidth
                            placeholder="Example: SQL filter template"
                        />
                        <TextField
                            label="Snippet Content"
                            value={snippetContent}
                            onChange={(event) => setSnippetContent(event.target.value)}
                            fullWidth
                            multiline
                            minRows={10}
                            placeholder="Paste text, SQL, JSON, or code here"
                        />
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setSnippetDialogOpen(false)} disabled={snippetSaving}>
                        Cancel
                    </Button>
                    <Button variant="contained" onClick={handleSnippetSave} disabled={snippetSaving}>
                        {snippetSaving ? 'Saving...' : 'Save Snippet'}
                    </Button>
                </DialogActions>
            </Dialog>

            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                    <CircularProgress />
                </Box>
            ) : (
                <>
                    <Box
                        role="tabpanel"
                        hidden={activeSharedTab !== 'snippets'}
                        id="shared-files-panel-snippets"
                        aria-labelledby="shared-files-tab-snippets"
                    >
                        {activeSharedTab === 'snippets' && (
                            <>
                                <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 0.5 }}>
                                    Shared Snippets
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                    Paste text or code once and let others copy it again later.
                                </Typography>
                                {snippets.length === 0 ? (
                                    <Paper variant="outlined" sx={{ p: 3, textAlign: 'center' }}>
                                        <Typography variant="body2" color="text.secondary">
                                            No shared snippets saved yet.
                                        </Typography>
                                    </Paper>
                                ) : (
                                    <Stack spacing={2}>
                                        {snippets.map((snippet) => (
                                            <Paper key={snippet.id} variant="outlined" sx={{ p: 2 }}>
                                                <Stack direction="row" spacing={1} alignItems="flex-start">
                                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                                        <Typography variant="subtitle2" fontWeight={700}>
                                                            {snippet.name}
                                                        </Typography>
                                                        <Typography variant="caption" color="text.secondary">
                                                            {snippet.size_chars} characters | {formatTimestamp(snippet.updated_at)}
                                                        </Typography>
                                                    </Box>
                                                    <Tooltip title="Copy">
                                                        <IconButton
                                                            size="small"
                                                            color="primary"
                                                            onClick={() => handleSnippetCopy(snippet)}
                                                            aria-label={`Copy ${snippet.name}`}
                                                        >
                                                            <CopyIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                    <Tooltip title="Delete">
                                                        <IconButton
                                                            size="small"
                                                            color="error"
                                                            onClick={() => handleSnippetDelete(snippet)}
                                                            aria-label={`Delete ${snippet.name}`}
                                                        >
                                                            <DeleteIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                </Stack>
                                                <Box
                                                    sx={{
                                                        mt: 1.5,
                                                        p: 1.5,
                                                        borderRadius: 1,
                                                        bgcolor: 'grey.100',
                                                        border: '1px solid',
                                                        borderColor: 'divider',
                                                        overflowX: 'auto',
                                                    }}
                                                >
                                                    <Typography
                                                        component="pre"
                                                        variant="body2"
                                                        sx={{
                                                            m: 0,
                                                            whiteSpace: 'pre-wrap',
                                                            wordBreak: 'break-word',
                                                            fontFamily: 'Monaco, Consolas, "Courier New", monospace',
                                                        }}
                                                    >
                                                        {snippet.content}
                                                    </Typography>
                                                </Box>
                                            </Paper>
                                        ))}
                                    </Stack>
                                )}
                            </>
                        )}
                    </Box>

                    <Box
                        role="tabpanel"
                        hidden={activeSharedTab !== 'files'}
                        id="shared-files-panel-files"
                        aria-labelledby="shared-files-tab-files"
                    >
                        {activeSharedTab === 'files' && (
                            <>
                                <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 0.5 }}>
                                    Uploaded Files
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                    Upload files for other users to download. Files are grouped by upload date.
                                </Typography>
                                {files.length === 0 ? (
                                    <Paper variant="outlined" sx={{ p: 3, textAlign: 'center' }}>
                                        <Typography variant="body2" color="text.secondary">
                                            No shared files uploaded yet.
                                        </Typography>
                                    </Paper>
                                ) : (
                                    <Stack spacing={2}>
                                        {groupedFiles.map(([dateKey, group]) => (
                                            <Paper key={dateKey} variant="outlined" sx={{ p: 2 }}>
                                                <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
                                                    {formatDateLabel(dateKey)} ({group.length})
                                                </Typography>
                                                <Divider sx={{ mb: 1 }} />
                                                <Stack spacing={1}>
                                                    {group.map((file) => (
                                                        <Box
                                                            key={file.id}
                                                            sx={{
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                gap: 1.5,
                                                                p: 1,
                                                                borderRadius: 1,
                                                                bgcolor: 'background.default',
                                                            }}
                                                        >
                                                            <FileIcon color="action" fontSize="small" />
                                                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                                                <Typography variant="body2" fontWeight={600} noWrap>
                                                                    {file.original_name}
                                                                </Typography>
                                                                <Typography variant="caption" color="text.secondary">
                                                                    {formatBytes(file.size_bytes)} | {formatTimestamp(file.uploaded_at)}
                                                                </Typography>
                                                            </Box>
                                                            <Tooltip title="Download">
                                                                <IconButton
                                                                    component="a"
                                                                    href={getSharedFileDownloadUrl(file.id)}
                                                                    size="small"
                                                                    color="primary"
                                                                    aria-label={`Download ${file.original_name}`}
                                                                >
                                                                    <DownloadIcon fontSize="small" />
                                                                </IconButton>
                                                            </Tooltip>
                                                            <Tooltip title="Delete">
                                                                <IconButton
                                                                    size="small"
                                                                    color="error"
                                                                    onClick={() => handleFileDelete(file)}
                                                                    aria-label={`Delete ${file.original_name}`}
                                                                >
                                                                    <DeleteIcon fontSize="small" />
                                                                </IconButton>
                                                            </Tooltip>
                                                        </Box>
                                                    ))}
                                                </Stack>
                                            </Paper>
                                        ))}
                                    </Stack>
                                )}
                            </>
                        )}
                    </Box>
                </>
            )}
        </Box>
    );
}

export default SharedFilesManager;
